import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { ArrowLeft, CalendarClock, Loader2, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

export type DayKey = "seg" | "ter" | "qua" | "qui" | "sex" | "sab" | "dom";
export type Period = "manha" | "tarde" | "noite";
export type Location = "academia" | "casa" | "ar_livre";

const DAYS: { key: DayKey; label: string }[] = [
  { key: "seg", label: "Seg" },
  { key: "ter", label: "Ter" },
  { key: "qua", label: "Qua" },
  { key: "qui", label: "Qui" },
  { key: "sex", label: "Sex" },
  { key: "sab", label: "Sáb" },
  { key: "dom", label: "Dom" },
];

const PERIODS: { key: Period; label: string }[] = [
  { key: "manha", label: "Manhã" },
  { key: "tarde", label: "Tarde" },
  { key: "noite", label: "Noite" },
];

const LOCATIONS: { key: Location; label: string }[] = [
  { key: "academia", label: "Academia" },
  { key: "casa", label: "Em casa" },
  { key: "ar_livre", label: "Ar livre" },
];

export interface AvailabilityState {
  daysPerWeek: number;
  minutesPerSession: number;
  preferredDays: DayKey[];
  period?: Period;
  location?: Location;
  observations: string;
}

interface AvailabilityStepProps {
  value: AvailabilityState;
  onChange: (next: AvailabilityState) => void;
  onBack: () => void;
  onGenerate: () => void;
  generating: boolean;
}

export function AvailabilityStep({
  value,
  onChange,
  onBack,
  onGenerate,
  generating,
}: AvailabilityStepProps) {
  const totalMinutes = value.daysPerWeek * value.minutesPerSession;

  const toggleDay = (day: DayKey) => {
    const has = value.preferredDays.includes(day);
    onChange({
      ...value,
      preferredDays: has
        ? value.preferredDays.filter((d) => d !== day)
        : [...value.preferredDays, day],
    });
  };

  return (
    <Card className="border-border/60">
      <CardHeader>
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10 text-primary">
            <CalendarClock className="size-5" />
          </div>
          <div className="flex flex-col gap-1">
            <CardTitle className="text-xl">Sua disponibilidade</CardTitle>
            <p className="text-sm text-muted-foreground">
              Conte como funciona sua rotina para personalizarmos o plano.
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        {/* Dias por semana */}
        <section className="flex flex-col gap-2">
          <label className="text-sm font-medium">Quantos dias por semana será o treino?</label>
          <ToggleGroup
            type="single"
            value={String(value.daysPerWeek)}
            onValueChange={(v) => v && onChange({ ...value, daysPerWeek: Number(v) })}
            className="justify-start flex-wrap"
          >
            {[2, 3, 4, 5, 6].map((n) => (
              <ToggleGroupItem key={n} value={String(n)} className="px-4">
                {n} dias
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </section>

        {/* Tempo por sessão */}
        <section className="flex flex-col gap-2">
          <label className="text-sm font-medium">Quanto tempo por sessão?</label>
          <ToggleGroup
            type="single"
            value={String(value.minutesPerSession)}
            onValueChange={(v) => v && onChange({ ...value, minutesPerSession: Number(v) })}
            className="justify-start flex-wrap"
          >
            {[30, 45, 60, 75, 90].map((n) => (
              <ToggleGroupItem key={n} value={String(n)} className="px-4">
                {n} min
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <p className="text-xs text-muted-foreground mt-1">
            Total: {value.daysPerWeek}× {value.minutesPerSession} min ={" "}
            <span className="font-medium text-foreground">{totalMinutes} min/semana</span>
          </p>
        </section>

        {/* Dias preferidos */}
        <section className="flex flex-col gap-2">
          <label className="text-sm font-medium">Dias preferidos da semana</label>
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map((d) => {
              const active = value.preferredDays.includes(d.key);
              return (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => toggleDay(d.key)}
                  className={cn(
                    "px-3.5 py-1.5 rounded-full text-sm border transition-colors",
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card text-foreground border-border hover:border-primary/40",
                  )}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Período do dia */}
        <section className="flex flex-col gap-2">
          <label className="text-sm font-medium">Período do dia preferido</label>
          <div className="flex flex-wrap gap-1.5">
            {PERIODS.map((p) => {
              const active = value.period === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() =>
                    onChange({ ...value, period: active ? undefined : p.key })
                  }
                  className={cn(
                    "px-4 py-1.5 rounded-full text-sm border transition-colors",
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card text-foreground border-border hover:border-primary/40",
                  )}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Local */}
        <section className="flex flex-col gap-2">
          <label className="text-sm font-medium">Onde será o treino?</label>
          <div className="flex flex-wrap gap-1.5">
            {LOCATIONS.map((l) => {
              const active = value.location === l.key;
              return (
                <button
                  key={l.key}
                  type="button"
                  onClick={() =>
                    onChange({ ...value, location: active ? undefined : l.key })
                  }
                  className={cn(
                    "px-4 py-1.5 rounded-full text-sm border transition-colors",
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card text-foreground border-border hover:border-primary/40",
                  )}
                >
                  {l.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Observações */}
        <section className="flex flex-col gap-2">
          <label className="text-sm font-medium">Observações (opcional)</label>
          <Textarea
            rows={3}
            placeholder="Ex: tenho desconforto no ombro direito, evito impacto..."
            value={value.observations}
            onChange={(e) => onChange({ ...value, observations: e.target.value })}
          />
        </section>

        <div className="flex items-center justify-between pt-2 gap-2 flex-wrap">
          <Button variant="ghost" onClick={onBack} disabled={generating}>
            <ArrowLeft data-icon="inline-start" />
            Voltar
          </Button>
          <Button onClick={onGenerate} disabled={generating} size="lg">
            {generating ? (
              <>
                <Loader2 data-icon="inline-start" className="animate-spin" />
                Gerando plano...
              </>
            ) : (
              <>
                <Sparkles data-icon="inline-start" />
                Gerar plano de treino
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export const formatAvailabilityForNotes = (
  state: AvailabilityState,
  selectedObjectives: string[],
): string => {
  const dayLabels = state.preferredDays
    .map((k) => DAYS.find((d) => d.key === k)?.label)
    .filter(Boolean)
    .join("/");
  const periodLabel = state.period ? PERIODS.find((p) => p.key === state.period)?.label : null;
  const locLabel = state.location ? LOCATIONS.find((l) => l.key === state.location)?.label : null;
  const lines = [
    `[Disponibilidade]`,
    `Dias por semana: ${state.daysPerWeek}`,
    `Tempo por sessão: ${state.minutesPerSession} min`,
    dayLabels ? `Dias preferidos: ${dayLabels}` : null,
    periodLabel ? `Período: ${periodLabel}` : null,
    locLabel ? `Local: ${locLabel}` : null,
    selectedObjectives.length > 0 ? `Objetivos: ${selectedObjectives.join(", ")}` : null,
    state.observations.trim() ? `Observações: ${state.observations.trim()}` : null,
  ].filter(Boolean);
  return lines.join("\n");
};
